import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {getPosterDuration} from '@Components/poster-editor/poster-editor-reducer';
import BaseTimeline from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/base-timeline/base-timeline';
import {Timestamps} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/timestamps/timestamps';
import {
  updateBottomShadowState,
  updatePosterMinWidthAccordingToDuration,
  updateTimelineFitWidthPerSecond,
  updateTimelineWidthPerSecond,
  updateTimelineHoverPosition,
} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.reducer';
import {useTopBottomScrollShadow} from '@Hooks/useTopBottomScrollShadow';
import {PosterBottomBarSeekbar} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/poster-bottom-bar-seekbar/poster-bottom-bar-seekbar';
import {isEditorMobileVariant} from '@Components/poster-editor/library/poster-editor-library';
import styles from './timeline.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {TIME_LINE_LEFT_MARGIN} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/timeline/timeline.types';

export function Timeline(): ReactElement<any> | null {
  const posterDuration = useAppSelector(getPosterDuration);
  const currentPageTime = useAppSelector((state) => {
    return state.posterEditor.currentTime;
  });
  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const initialPageDuration = useRef(posterDuration);
  const widthPerSecond = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.widthPerSecond;
  });
  const [transform, setTransform] = useState(0);
  const [delayMovement, setDelayMovement] = useState(false);
  const dispatch = useAppDispatch();
  const {bottomShadow} = useTopBottomScrollShadow(timelineContainerRef);
  const isPanning = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.isPanning;
  });
  const isDesignItemResizing = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.isDesignResizing;
  });
  const durationWhileResizing = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.durationWhileResizing;
  });
  const maxDurationWhileResizing = useAppSelector((state) => {
    return state.posterEditorWebBottomBar.maxDurationWhileResizing;
  });
  const timeout = useRef<NodeJS.Timeout>(undefined);

  useEffect((): void => {
    if (bottomShadow) {
      timeout.current = setTimeout((): void => {
        dispatch(updateBottomShadowState(true));
      }, 500);
    }

    if (!bottomShadow) {
      clearTimeout(timeout.current);
      dispatch(updateBottomShadowState(false));
    }
  }, [bottomShadow]);

  const updateScrollPosition = (scrollVal: number): void => {
    setDelayMovement(true);
    timelineContainerRef?.current?.scrollTo({
      left: scrollVal,
      behavior: 'smooth',
    });
    setTimeout((): void => {
      setDelayMovement(false);
    }, 500);
  };

  const scrollIfNeeded = (): void => {
    if (delayMovement) {
      return;
    }
    if (!timelineContainerRef.current) {
      return;
    }

    const element = timelineContainerRef.current.getElementsByClassName('rc-slider-handle')[0];
    const right = element?.getBoundingClientRect().right;
    const width = timelineContainerRef.current.clientWidth;

    if (right < 0) {
      updateScrollPosition(timelineContainerRef.current.scrollLeft - right * -1 - 40);
    }
    if (right > width) {
      updateScrollPosition(timelineContainerRef.current.scrollLeft + timelineContainerRef.current.clientWidth);
    }
  };

  useEffect((): void => {
    scrollIfNeeded();
  }, [currentPageTime]);

  const getWidthPerSecond = (): void => {
    if (!timelineContainerRef?.current || !initialPageDuration.current) {
      return;
    }

    dispatch(updateTimelineWidthPerSecond((timelineContainerRef.current.clientWidth - 68) / initialPageDuration.current));
  };

  const calculateFitWidthPerSecond = (): void => {
    if (!timelineContainerRef?.current || !posterDuration) {
      return;
    }

    dispatch(updateTimelineFitWidthPerSecond((timelineContainerRef.current.clientWidth - 68) / posterDuration));
  };

  const calculateMinWidthThreshold = (): void => {
    if (!posterDuration) {
      return;
    }

    if (isEditorMobileVariant()) {
      const minTimelineWidthForMobile = 400;
      dispatch(updatePosterMinWidthAccordingToDuration(minTimelineWidthForMobile / posterDuration));
    }

    if (!timelineContainerRef?.current) {
      return;
    }

    dispatch(updatePosterMinWidthAccordingToDuration((timelineContainerRef.current.clientWidth * 0.4) / posterDuration));
  };

  useEffect((): void => {
    getWidthPerSecond();
  }, []);

  useEffect((): void => {
    calculateFitWidthPerSecond();
    calculateMinWidthThreshold();
  }, [posterDuration]);

  const onScroll = (): void => {
    if (timelineContainerRef.current) {
      setTransform(-1 * timelineContainerRef.current.scrollLeft);
    }
  };

  const calculateRelativeXFromMousePosition = (clientX: number): number | undefined => {
    if (!timelineContainerRef.current) {
      return undefined;
    }

    const rect = timelineContainerRef.current.getBoundingClientRect();
    const scrollLeft = timelineContainerRef.current.scrollLeft;
    return clientX - rect.left + scrollLeft;
  };

  const calculateTimeFromMousePosition = (clientX: number): number | undefined => {
    if (!timelineContainerRef.current) {
      return undefined;
    }

    const relativeX = calculateRelativeXFromMousePosition(clientX);
    if (relativeX === undefined || posterDuration === undefined) {
      return undefined;
    }

    const time = (relativeX - TIME_LINE_LEFT_MARGIN) / widthPerSecond;
    return Math.max(0, Math.min(time, posterDuration));
  };

  const handleMouseMove = (event: MouseEvent): void => {
    dispatch(updateTimelineHoverPosition(calculateRelativeXFromMousePosition(event.clientX)));
  };

  const handleMouseLeave = (): void => {
    dispatch(updateTimelineHoverPosition(undefined));
  };

  const handleClick = (event: MouseEvent): void => {
    void handleClickPromise(event);
  };

  const handleClickPromise = async (event: MouseEvent): Promise<void> => {
    const time = calculateTimeFromMousePosition(event.clientX);
    if (time !== undefined) {
      if (window.posterEditor?.whiteboard?.isPlaying()) {
        await window.posterEditor?.whiteboard?.pause();
      }
      await window.posterEditor?.whiteboard?.seek(time);
    }
  };

  useEffect(() => {
    const container = timelineContainerRef.current;
    if (!container) {
      return;
    }

    container.addEventListener('scroll', onScroll);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseleave', handleMouseLeave);
    container.addEventListener('click', handleClick);

    return (): void => {
      container.removeEventListener('scroll', onScroll);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);
      container.removeEventListener('click', handleClick);
    };
  }, [timelineContainerRef.current]);

  if (!posterDuration || !initialPageDuration.current) {
    return null;
  }

  const getWidthStyleForSlider = (): string | undefined => {
    if (isDesignItemResizing) {
      return `${durationWhileResizing * widthPerSecond}px`;
    }
    return `${widthPerSecond ? widthPerSecond * posterDuration : undefined}px`;
  };

  const getWidthStyle = (offset = 0): string | undefined => {
    if (isDesignItemResizing) {
      return `${Math.max(durationWhileResizing, maxDurationWhileResizing, posterDuration) * widthPerSecond + offset}px`;
    }
    return `${widthPerSecond ? widthPerSecond * posterDuration + offset : undefined}px`;
  };

  const getWidthStyleForTimestamps = (): string | undefined => {
    if (isDesignItemResizing) {
      return `${Math.ceil(Math.max(durationWhileResizing, maxDurationWhileResizing, posterDuration)) * widthPerSecond}px`;
    }
    return `${widthPerSecond ? widthPerSecond * Math.ceil(posterDuration) : undefined}px`;
  };

  return (
    <div id="timeline-container" ref={timelineContainerRef} className={`${styles.wrapperContainer} ${styles.scrollableContainer}`}>
      <div
        className={`${styles.expandedTimelineSeekbar}  ${!isPanning && !isDesignItemResizing ? styles.hasTransition : ''}`}
        style={{width: getWidthStyleForSlider(), transform: `translate(${transform}px, 0px)`}}
      >
        <PosterBottomBarSeekbar isOpenedInTimeline />
      </div>
      {/*<TimelineHoverLine />*/}
      <div
        style={{
          width: getWidthStyleForTimestamps(),
        }}
        className={`${styles.timestampContainer} ${isPanning || isDesignItemResizing ? '' : styles.hasTransition}`}
      >
        <Timestamps initialDuration={initialPageDuration.current} widthPerSecond={widthPerSecond} totalDuration={posterDuration} timelineContainerRef={timelineContainerRef} />
      </div>
      <div
        style={{
          width: getWidthStyle(60),
          minWidth: '100%',
        }}
        className={isPanning || isDesignItemResizing ? '' : styles.hasTransition}
      >
        <BaseTimeline />
      </div>
    </div>
  );
}
